import { authPermissions } from '@/constants'
import { routeService } from '@/services'
import store from '@/store'
import { Page } from '@/types/enums'
import { toDashSeparated } from '@/utils'
import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'

export async function generateVueRoute() : Promise<{router: VueRouter, routes: Array<RouteConfig>}> {
    const searchRoutes = await routeService.getSearchRoutes()

    Vue.use(VueRouter)

    const searchPages = [
        {
            path: '/property',
            name: Page.PROPERTY,
            meta: {
                navBar:1,
                text:'Property',
                requiredPermissions: [authPermissions.canAccessProperty]
            }
        },
        {
            path: '/unit',
            name: Page.UNITS,
            meta: {
                navBar:2,
                text:'Units',
                requiredPermissions: [authPermissions.canAccessUnits]
            }
        },
        {
            path: '/tenant',
            name: Page.TENANTS,
            meta:
            {
                navBar:3,
                text:'Tenants',
                requiredPermissions: [authPermissions.canAccessTenants]
            }
        },
        {
            path: '/vendor',
            name: Page.VENDORS,
            meta: {
                navBar:4,
                text:'Vendors',
                requiredPermissions: [authPermissions.canAccessVendors]
            }
        },
        {
            path: '/jobs',
            name: Page.JOBS,
            meta: {
                navBar:5,
                text:'Jobs',
                requiredPermissions: [authPermissions.canAccessJobs]
            }
        },
        {
            path: '/accounting',
            name:  Page.ACCOUNTING,
            meta: {
                navBar:6,
                text: 'Accounting',
                requiredPermissions: [authPermissions.canAccessAccounting]
            }
        },
        ...searchRoutes.map( route => ({
            path: `/${route.sourceType.toLowerCase()}/${toDashSeparated(route.pageName)}`,
            name: route.pageName,
            meta: {
                navBar: 7,
                text: route.pageAlias,
                requiredPermissions: route.requiredPermissions,
                group: `Data.${route.sourceType}`
            }
        }))
    ]

    const specialNavbarPages = [
         {
            path: '/calendar',
            name: Page.CALENDAR,
            component: () => import('@/views/Calendar.vue'),
            meta: {
                navBar: 1,
                text:'Calendar',
                requiredPermissions: [authPermissions.canAccessCalendar],
                excludePageConfig: true,
            }
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: {
            navBar: 0,
            text: 'Dashboard',
            requiredPermissions: [authPermissions.canAccessAllQuantumCharts],
            excludePageConfig: true,
          }  
        },
        {
            path: '/map',
            name: 'map',
            component: () => import('@/views/PropertyMapView.vue'),
            meta: {
                navBar:0,
                text:'Map',
                requiredPermissions: [authPermissions.canAccessProperty],
                excludePageConfig: true
            }
        },    
        {
            path: '/user',
            name: Page.USERS,
            component: () => import('@/views/User.vue'),
            meta: {
                navBar:7,
                text:'Users',
                requiredPermissions: [authPermissions.canManageUsers],
                group: 'Admin'
            }
        },
        {
            path: '/userRoles',
            name: Page.USER_ROLES,
            component: () => import('@/views/UserRole.vue'),
            meta: {
                navBar:8,
                text:'User Roles',
                requiredPermissions: [authPermissions.canManageUsers],
                group: 'Admin'
            }
        },
        {
            path: '/customers',
            name: Page.CLIENTS,
            component: () => import('@/views/Client.vue'),
            meta: {
                navBar:8,
                text:'Customers',
                requiredPermissions: [authPermissions.canManageClients],
                group: 'Admin'
            }
        },
        {
            path: '/customerDataSources',
            name: Page.CLIENT_DATA_SOURCES,
            component: () => import('@/views/ClientDataSource.vue'),
            meta: {
                navBar:8,
                text:'Customer Data Sources',
                requiredPermissions: [authPermissions.canManageClients],
                group: 'Admin'
            }
        },
        {
            path: '/setting/:subPage?',
            name: 'setting',
            component: () => import('@/views/Setting.vue'),
            meta: {
                navBar:9,
                text:'Settings',
                requiredPermissions: [authPermissions.canAccessAllRelatedSettings],
                excludePageConfig: true,
                group: 'Admin'
            }
        },
        // {
        //     path: '/upload',
        //     name: 'upload',
        //     component: () => import('@/views/Upload.vue'),
        //     meta: {
        //         navBar:10,
        //         text:'Upload',
        //         requiredPermissions: [authPermissions.canUseUpload]
        //     }
        // }
    ]

    const navbarPages = [ 
        ...searchPages.map(page => ({
            ...page,
            component: () => import('@/views/GeneralSearchView.vue'),
        }))
    , ...specialNavbarPages].sort( (x,y) => x.meta.navBar - y.meta.navBar)

    const routes: Array<RouteConfig> = [
        {
            path: '/',
            name: 'home',
            component: () => import('@/views/Home.vue'),
            children: [
                ...navbarPages,
                {
                    path: '/property/:propertyID',
                    name: 'propertyDetail',
                    component: () => import('@/views/PropertyDetail.vue'),
                    meta: {
                        excludePageConfig: true
                    }
                },
                {
                    path: '/tenant/:tenantID',
                    name: 'tenantDetail',
                    component: () => import('@/views/TenantDetail.vue'),
                    meta: {
                        excludePageConfig: true
                    }
                },
                {
                    path: '/release-notes',
                    name: 'releaseNotes',
                    component: () => import('@/views/ReleaseNotes.vue'),
                    meta: {
                        text:'Release Notes',
                        excludePageConfig: true
                    }
                },
                {
                    path: '/',
                    name: 'redirectHome',
                    redirect: { name: 'dashboard'}
                },
                {
                    path: '*',
                    name: 'notFound',
                    component: () => import('@/views/NotFound.vue'),
                    meta: {
                        text:'Not Found',
                        excludePageConfig: true
                    }
                }
            ]
        },

    ]

    const router = new VueRouter({
        mode: 'history',
        routes,
    })

    router.beforeEach( async(to, from, next) => {
        await store.dispatch('updateCurrentPage', to.name ?? 'none')
        if(!to.meta?.excludePageConfig) {
            await store.dispatch('getCurrentPageConfig')
        }
        await store.dispatch('forbidUser', false)
        
        next()
    } )
    
    return {router, routes}
}
